import React, { createContext, ReactNode, useContext, useState } from 'react';

import { useShopDataModalContext } from 'components/ShopData/ShopDataModalContext';

import { CommunityContract } from '../../types/contracts';

type ContractContentType = {
  keys: string[];
  selectedKey: string | undefined;
  contracts: (CommunityContract | undefined)[];
  selectedContract: CommunityContract | undefined;
  openModal: boolean;
  setKeys: (keys: string[]) => void;
  setSelectedKey: (key: string | undefined) => void;
  setContracts: (contracts: CommunityContract[]) => void;
  setSelectedContract: (data: CommunityContract) => void;
  setOpenModal: (open: boolean) => void;
  handleAvailableContracts: (contractsKeys: string[], selectedKey: string | undefined) => void;
};

const ContractContext = createContext<ContractContentType | undefined>(undefined);

export const useContractContext = () => {
  const context = useContext(ContractContext);
  if (!context) throw new Error('useContractContext must be used within ContractProvider');
  return context;
};

export const ContractProvider = ({ children }: { children: ReactNode }) => {
  const { community, isCommunityCcrc } = useShopDataModalContext();

  const [keys, setKeys] = useState<string[]>([]);
  const [selectedKey, setSelectedKey] = useState<string | undefined>(undefined);
  const [contracts, setContracts] = useState<(CommunityContract | undefined)[]>([]);
  const [selectedContract, setSelectedContract] = useState<CommunityContract | undefined>(undefined);
  const [openModal, setOpenModal] = useState(false);

  const handleAvailableContracts = (contractsGroups: string[], selectedKey: string | undefined) => {
    if (isCommunityCcrc) {
      const splitContracts = contractsGroups.flatMap((item) => item.split('|'));
      const allContractKeys = Array.from(new Set([...splitContracts, ...(selectedKey ? selectedKey.split('') : [])]));

      const availableContracts = (allContractKeys ?? {})
        .map((contractSlug: string) => community?.contracts.find((c) => c.slug === contractSlug))
        .filter(Boolean);
      setContracts(availableContracts);

      if (availableContracts?.length) {
        let newKeys = contractsGroups;
        if (selectedKey && contractsGroups.includes(selectedKey)) {
          newKeys = [
            selectedKey,
            ...contractsGroups.filter((slug) => (selectedKey ? !selectedKey.includes(slug) : true)),
          ];
        }
        setKeys(newKeys);
        setSelectedKey(selectedKey && newKeys.includes(selectedKey) ? selectedKey : contractsGroups[0]);
      } else {
        setKeys([]);
        setSelectedKey(undefined);
      }
    } else if (community?.contracts?.length) {
      // Handle RENTAL communities
      setSelectedKey(community?.contracts[0].slug);
      setKeys([community?.contracts[0].slug]);
    }
  };

  return (
    <ContractContext.Provider
      value={{
        keys,
        selectedKey,
        contracts,
        selectedContract,
        openModal,
        setKeys,
        setSelectedKey,
        setContracts,
        setSelectedContract,
        setOpenModal,
        handleAvailableContracts,
      }}
    >
      {children}
    </ContractContext.Provider>
  );
};
